import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { EnvironmentService } from '@nomoh-monorepo/shared-data-access-environment';

/**
 * Interceptor HTTP global que adiciona configurações padrão em todas as requisições
 *
 * Funcionalidades:
 * - Adiciona `withCredentials: true` (cookies/sessões) **APENAS para APIs internas**
 * - Adiciona header `Accept-Language` com idioma atual do sistema
 * - Adiciona header `Content-Type: application/json` se body existe e não é FormData
 *
 * APIs externas (ViaCEP, etc) **NÃO** recebem credenciais para evitar erro CORS
 * Os domínios internos são configurados por ambiente em `environment.internalDomains`
 *
 * @example
 * // app.config.ts
 * export const appConfig: ApplicationConfig = {
 *   providers: [
 *     provideHttpClient(withInterceptors([httpConfigInterceptor]))
 *   ]
 * };
 */
export const httpConfigInterceptor: HttpInterceptorFn = (req, next) => {
  // ✅ inject() funciona dentro de HttpInterceptorFn (Angular 16+)
  const environmentService = inject(EnvironmentService);

  // Obtém idioma diretamente do localStorage (evita dependência circular com I18nService)
  const currentLanguage = getLanguageFromStorage();

  // Obtém domínios internos do environment (configurável por ambiente)
  const internalDomains = environmentService.getInternalDomains();

  // Detecta se é uma requisição para API externa
  const isExternalApi = isExternalRequest(req.url, internalDomains);

  // Clone a requisição com configurações padrão
  const modifiedReq = req.clone({
    // ✅ Só envia credenciais para APIs internas (evita erro CORS com ViaCEP, etc)
    withCredentials: !isExternalApi,
    setHeaders: {
      'Accept-Language': currentLanguage,
      // Só adiciona Content-Type se não for FormData (upload de arquivos)
      ...(req.body && !(req.body instanceof FormData)
        ? { 'Content-Type': 'application/json' }
        : {}),
    },
  });

  return next(modifiedReq);
};

/**
 * Verifica se a URL é de uma API externa (não pertence ao monorepo)
 * @param url URL da requisição
 * @param internalDomains Lista de domínios internos do environment
 * @returns true se for API externa
 */
function isExternalRequest(url: string, internalDomains: string[]): boolean {
  // Requisições relativas (/api/...) = internas
  if (url.startsWith('/') || url.startsWith('./')) {
    return false;
  }

  // Requisições absolutas = verificar domínio
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname;

    // Se hostname está na lista de domínios internos ou é subdomínio = não é externa
    if (
      internalDomains.some(
        (domain) => hostname === domain || hostname.endsWith(`.${domain}`)
      )
    ) {
      return false;
    }

    // Qualquer outro domínio = API externa
    return true;
  } catch {
    // URL inválida = tratar como interna (fallback seguro)
    return false;
  }
}

const IDIOMAS_SUPORTADOS = ['pt', 'en', 'es', 'fr'];

/**
 * Mesma prioridade do I18nService.getStoredLanguage(), sem depender dele.
 * Escolha manual do seletor → primeiro idioma suportado do navegador → pt
 */
function getLanguageFromStorage(): string {
  try {
    const escolhaManual = localStorage.getItem('app-language-manual') === '1';
    const stored = localStorage.getItem('app-language');
    if (escolhaManual && stored && IDIOMAS_SUPORTADOS.includes(stored)) {
      return stored;
    }
  } catch {
    // localStorage não disponível (SSR, modo privado, etc)
  }

  const lista = navigator.languages?.length
    ? Array.from(navigator.languages)
    : [navigator.language];
  const suportado = lista
    .map((item) => item?.split(/[-_]/)[0]?.toLowerCase())
    .find((code) => !!code && IDIOMAS_SUPORTADOS.includes(code));
  return suportado || 'pt';
}
